import { useSettingsStore } from './settingsStore';
import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { QuoteTemplate } from '../types';

interface QuoteTemplateState {
  templates: QuoteTemplate[];
  loading: boolean;
  error: string | null;
  fetchTemplates: () => Promise<void>;
  createTemplate: (template: Omit<QuoteTemplate, 'id' | 'created_at' | 'updated_at' | 'organization_id'>) => Promise<QuoteTemplate>;
  updateTemplate: (template: QuoteTemplate) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
}

/**
 * Riscrive l'elenco dei servizi nell'ambito di un template.
 *
 * Cancella e reinserisce invece di fare un diff: le righe sono poche (un
 * template copre al massimo qualche decina di servizi) e il diff introdurrebbe
 * uno stato intermedio in cui l'ambito e' parziale.
 *
 * `undefined` significa "non toccare l'ambito"; una lista vuota lo azzera.
 */
async function replaceTemplateScope(
  templateId: string,
  organizationId: string | undefined,
  serviceIds: string[] | undefined,
): Promise<void> {
  if (serviceIds === undefined) return;

  let orgId = organizationId;
  if (!orgId) {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData?.user) throw new Error('User not authenticated');
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', userData.user.id)
      .single();
    orgId = profile?.organization_id;
  }
  if (!orgId) throw new Error('Organizzazione non determinata: ambito non salvato');

  const { error: delErr } = await supabase
    .from('quote_template_services')
    .delete()
    .eq('template_id', templateId);
  if (delErr) throw new Error(`Errore aggiornando l'ambito: ${delErr.message}`);

  const unici = Array.from(new Set(serviceIds.filter(Boolean)));
  if (unici.length === 0) return;

  const { error: insErr } = await supabase.from('quote_template_services').insert(
    unici.map((service_id) => ({ template_id: templateId, service_id, organization_id: orgId })),
  );
  if (insErr) throw new Error(`Errore aggiornando l'ambito: ${insErr.message}`);
}

export const useQuoteTemplateStore = create<QuoteTemplateState>((set, get) => ({
  templates: [],
  loading: false,
  error: null,

  fetchTemplates: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        set({ loading: false, error: 'User not authenticated' });
        return;
      }

      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError || !userProfile?.organization_id) {
        set({ 
          loading: false, 
          error: profileError?.message || 'User not associated with an organization' 
        });
        return;
      }

      const organizationId = useSettingsStore.getState().organizationId || userProfile.organization_id;
      const { data, error } = await supabase
        .from('quote_templates')
        .select('*')
        .eq('organization_id', organizationId)
        .is('archived_at', null) // soft-delete: nascondi archiviati
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[QuoteTemplateStore] Error fetching templates:', error);
        set({ loading: false, error: friendlyDbError(error) });
        return;
      }

      // Ambito per servizio: una query sola per tutta l'organizzazione, poi si
      // raggruppa in memoria. Sono poche righe (i template per org sono 1-5).
      const { data: scopeRows } = await supabase
        .from('quote_template_services')
        .select('template_id, service_id')
        .eq('organization_id', organizationId);

      const byTemplate = new Map<string, string[]>();
      for (const row of scopeRows || []) {
        const list = byTemplate.get(row.template_id) || [];
        list.push(row.service_id);
        byTemplate.set(row.template_id, list);
      }

      const templates = (data || []).map((t: QuoteTemplate) => ({
        ...t,
        scopedServiceIds: byTemplate.get(t.id) || [],
      }));

      if (useSettingsStore.getState().organizationId && useSettingsStore.getState().organizationId !== organizationId) return;
      set({ templates, loading: false });
    } catch (error) {
      console.error('[QuoteTemplateStore] Unexpected error:', error);
      set({ loading: false, error: friendlyDbError(error) });
    }
  },

  createTemplate: async (template) => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        throw new Error('User not authenticated');
      }

      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (profileError || !userProfile?.organization_id) {
        throw new Error(profileError?.message || 'User not associated with an organization');
      }

      const organizationId = useSettingsStore.getState().organizationId || userProfile.organization_id;

      // Log the template data for debugging
      console.log('[QuoteTemplateStore] Creating template:', { 
        name: template.name,
        description: template.description?.length || 0,
        content: template.content?.length || 0,
        header: template.header?.length || 0,
        footer: template.footer?.length || 0
      });

      // Sanitize template data before sending to Supabase
      const sanitizedTemplate: Record<string, unknown> = {
        name: template.name,
        document_kind: template.document_kind ?? 'quote',
        description: template.description || null,
        content: template.content || '',
        header: template.header || null,
        footer: template.footer || null,
        organization_id: organizationId
      };
      if ('margin_top' in template) sanitizedTemplate.margin_top = (template as any).margin_top;
      if ('margin_bottom' in template) sanitizedTemplate.margin_bottom = (template as any).margin_bottom;
      if ('terms_font_size_pt' in template) sanitizedTemplate.terms_font_size_pt = (template as any).terms_font_size_pt;
      // ⚠️ Questa whitelist e' esplicita: un campo non elencato NON dà errore,
      // viene semplicemente scartato in silenzio. Aggiungere qui ogni colonna nuova.
      if ('service_scope' in template) sanitizedTemplate.service_scope = (template as any).service_scope;
      if ('applies_to_custom_lines' in template) {
        sanitizedTemplate.applies_to_custom_lines = (template as any).applies_to_custom_lines;
      }

      console.log('[QuoteTemplateStore] Sending sanitized template data to Supabase');

      const { data, error } = await supabase
        .from('quote_templates')
        .insert([sanitizedTemplate])
        .select()
        .single();

      if (error) {
        console.error('[QuoteTemplateStore] Error creating template:', error);
        throw new Error(`Errore durante la creazione: ${error.message}`);
      }

      const scopedServiceIds = (template as any).scopedServiceIds as string[] | undefined;
      await replaceTemplateScope(data.id, organizationId, scopedServiceIds);

      console.log('[QuoteTemplateStore] Template created successfully:', data);

      // Update the templates list
      set(state => ({
        templates: [{ ...data, scopedServiceIds: scopedServiceIds ?? [] }, ...state.templates],
        loading: false
      }));

      return data;
    } catch (error) {
      console.error('[QuoteTemplateStore] Error creating template:', error);
      set({ loading: false, error: friendlyDbError(error) });
      throw error;
    }
  },

  updateTemplate: async (template) => {
    set({ loading: true, error: null });
    try {
      console.log('[QuoteTemplateStore] Updating template:', { 
        id: template.id, 
        name: template.name,
        description: template.description?.length || 0,
        content: template.content?.length || 0,
        header: template.header?.length || 0,
        footer: template.footer?.length || 0
      });

      // Sanitizing template data before sending to Supabase
      const dataToUpdate: Record<string, unknown> = {
        name: template.name,
        description: template.description || null,
        content: template.content || '',
        header: template.header || null,
        footer: template.footer || null
      };
      if ('margin_top' in template) dataToUpdate.margin_top = (template as any).margin_top;
      if ('margin_bottom' in template) dataToUpdate.margin_bottom = (template as any).margin_bottom;
      if ('terms_font_size_pt' in template) dataToUpdate.terms_font_size_pt = (template as any).terms_font_size_pt;
      // ⚠️ Vedi la nota sulla whitelist in createTemplate: i campi non elencati
      // qui vengono scartati senza errore.
      if ('service_scope' in template) dataToUpdate.service_scope = (template as any).service_scope;
      if ('applies_to_custom_lines' in template) {
        dataToUpdate.applies_to_custom_lines = (template as any).applies_to_custom_lines;
      }

      const { error, data } = await supabase
        .from('quote_templates')
        .update(dataToUpdate)
        .eq('id', template.id)
        .select();

      if (error) {
        console.error('[QuoteTemplateStore] Error updating template:', error);
        throw new Error(`Errore durante l'aggiornamento: ${error.message}`);
      }

      if ('scopedServiceIds' in template) {
        await replaceTemplateScope(
          template.id,
          template.organization_id,
          (template as any).scopedServiceIds as string[] | undefined,
        );
      }

      console.log('[QuoteTemplateStore] Template updated successfully:', data);

      // Update the templates list
      set(state => ({
        templates: state.templates.map(t => 
          t.id === template.id ? { ...t, ...template } : t
        ),
        loading: false
      }));
    } catch (error) {
      console.error('[QuoteTemplateStore] Error updating template:', error);
      set({ loading: false, error: friendlyDbError(error) });
      throw error;
    }
  },

  // SOFT-DELETE: archivia invece di cancellare. Il DELETE fisico fallisce con
  // FK constraint da signature_requests (richieste firma storiche collegate al
  // template). Settiamo archived_at = now() e il template scompare dalle liste
  // attive (fetchTemplates filtra `is('archived_at', null)`) e dalla selezione
  // in generazione preventivo, mantenendo l'integrità referenziale storica.
  deleteTemplate: async (id) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('quote_templates')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        console.error('[QuoteTemplateStore] Error archiving template:', error);
        throw new Error(error.message);
      }

      // Rimuovi dall'array locale (l'utente non lo vede più dopo l'archivio)
      set(state => ({
        templates: state.templates.filter(t => t.id !== id),
        loading: false
      }));
    } catch (error) {
      console.error('[QuoteTemplateStore] Error archiving template:', error);
      set({ loading: false, error: friendlyDbError(error) });
      throw error;
    }
  }
}));